/**
 * GDPR 1.4 CMSR — Politica de utilizare a Modulelor Cookie
 *
 * Text adaptat exclusiv după modelul oficial CMSR (GDPR-1.4 ver. 1.0).
 * Tabelul de cookies conține exclusiv elementele identificate în auditul
 * tehnic al website-ului ro-dental.ro (august 2026). Nu se listează cookies
 * inexistente. Categoriile „Statistici" și „Marketing" NU se afișează
 * pentru că website-ul nu utilizează astfel de tehnologii.
 */
import { OPERATOR } from "../lib/operator";
import { Link } from "react-router-dom";

export default function CookiePolicy() {
  const { reopenPreferences } = { reopenPreferences: null }; // opened via footer link

  return (
    <div className="bg-warm-gradient min-h-screen">
      <section className="max-w-4xl mx-auto container-x py-16 sm:py-20">
        <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Website</p>
        <h1 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="cookie-policy-title">
          Politica de utilizare a Modulelor Cookie
        </h1>
        <p className="mt-2 text-xs text-brand-navy/55 italic">Versiune aplicabilă de la data de {OPERATOR.versionDate}</p>

        <div className="prose max-w-none prose-headings:font-display prose-headings:text-brand-navy prose-p:text-brand-navy/85 prose-li:text-brand-navy/85 prose-strong:text-brand-navy mt-10 space-y-6 leading-relaxed">

          <p>
            Prezenta Politica de utilizare a Modulelor Cookie se aplica tuturor vizitatorilor website-ului <strong>{OPERATOR.website}</strong> („Website-ul") detinut si operat de <strong>{OPERATOR.legalName}</strong>, cu sediul in <strong>{OPERATOR.address}</strong>, inregistrata in Registrul Unic al Cabinetelor Medicale sub nr. <strong>{OPERATOR.rucm}</strong>, avand Cod Unic de Inregistrare <strong>{OPERATOR.cui}</strong> („Cabinetul medical").
          </p>
          <p>
            In cuprinsul acestei Politici veti gasi detalii referitoare la ce reprezinta modulele cookie, in ce scop sunt folosite si care sunt implicatiile pentru utilizatori ca urmare a acceptarii anumitor categorii de module cookie plasate pe Website.
          </p>

          <h2>Ce sunt modulele cookies?</h2>
          <p>
            Noțiunea de „cookies" se referă la modulele cookie și la orice alte tehnologii similare (precum spații locale de stocare — <em>localStorage</em>, <em>sessionStorage</em>) prin intermediul cărora pot fi colectate automat informații.
          </p>
          <p>
            Cookie-urile sunt fișiere text de mici dimensiuni plasate de Website, prin intermediul browser-ului, pe dispozitivul pe care îl utilizați pentru navigare (calculator, smartphone, etc.), care identifică dispozitivul în momentul accesării Website-ului. La fiecare accesare ulterioară a Website-ului, browserul trimite către serverul Website-ului acest fișier, permițând astfel identificarea unui vizitator care a revenit pe site.
          </p>

          <h2>Rolul Modulelor cookie</h2>
          <p>
            Pe Website-ul <strong>{OPERATOR.websiteDisplay}</strong>, modulele cookie și tehnologiile similare sunt utilizate <strong>strict</strong> pentru: (a) a asigura funcționarea corectă a website-ului, (b) a permite accesul securizat la panoul de administrare pentru personalul cabinetului și (c) a permite, doar cu consimțământul dumneavoastră expres, încărcarea conținutului terț (harta Google Maps).
          </p>
          <p>
            <strong>NU utilizăm cookie-uri pentru statistici, analiză, cercetare, publicitate personalizată sau marketing.</strong> Nu integrăm Google Analytics, Meta/Facebook Pixel sau alte instrumente similare.
          </p>

          <h2>Tipuri de module cookie</h2>

          <h3>În funcție de durată</h3>
          <ul>
            <li><strong>Cookie de sesiune</strong> — durata de stocare este limitată la sesiunea în care vizitatorul accesează Website-ul, fiind șters automat la închiderea browserului.</li>
            <li><strong>Cookie permanent</strong> — este stocat la nivelul dispozitivului utilizatorului și nu este șters la închiderea browserului.</li>
          </ul>

          <h3>În funcție de furnizor</h3>
          <ul>
            <li><strong>Cookie-uri proprii</strong> — plasate direct de Cabinetul medical prin website.</li>
            <li><strong>Cookie-uri de la terți</strong> — plasate de furnizori de conținut terț încorporat (în cazul nostru, Google, prin iframe-ul cu harta), în situația în care utilizatorul a activat categoria „Conținut terț".</li>
          </ul>

          <h3>În funcție de scop</h3>
          <ul>
            <li>
              <strong>Module cookie necesare</strong> — vă permit să navigați pe website și să utilizați funcțiile acestuia. Sunt esențiale pentru buna funcționare a website-ului și nu pot fi dezactivate. Aceste module nu colectează informații despre dumneavoastră care ar putea fi folosite pentru publicitate sau pentru înregistrarea activității de navigare.
            </li>
            <li>
              <em className="text-brand-navy/60">Module cookie pentru statistici — de cercetare și analiză: <strong>NU se utilizează</strong>.</em>
            </li>
            <li>
              <em className="text-brand-navy/60">Module cookie pentru marketing: <strong>NU se utilizează</strong>.</em>
            </li>
            <li>
              <strong>Conținut terț încorporat</strong> — atunci când optați pentru afișarea hărții Google, iframe-ul Google Maps se poate încărca cu cookie-uri proprii Google, conform politicilor Google.
            </li>
          </ul>

          <h2>Module cookie plasate pe Website-ul nostru</h2>

          <h3>Module cookie necesare</h3>
          <div className="overflow-x-auto not-prose">
            <table className="min-w-full text-sm border border-brand-beige rounded-xl overflow-hidden">
              <thead className="bg-brand-cream text-brand-navy">
                <tr>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Nume</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Scop</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Furnizor</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Tip</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Retenție</th>
                </tr>
              </thead>
              <tbody className="bg-white text-brand-navy/85">
                <tr className="border-b border-brand-beige">
                  <td className="p-3 font-mono text-xs">rodental_token</td>
                  <td className="p-3">Menținerea autentificării în panoul de administrare al cabinetului (utilizat exclusiv de personalul cabinetului, nu de pacienți)</td>
                  <td className="p-3">Website (proprietar: Cabinet medical)</td>
                  <td className="p-3">localStorage (proprietar)</td>
                  <td className="p-3">Până la delogare</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono text-xs">rodental_cookie_consent</td>
                  <td className="p-3">Memorarea alegerilor dumneavoastră privind cookie-urile, pentru a respecta preferințele exprimate</td>
                  <td className="p-3">Website (proprietar: Cabinet medical)</td>
                  <td className="p-3">localStorage (proprietar)</td>
                  <td className="p-3">Permanent (până la ștergerea manuală sau retragerea consimțământului)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="mt-8">Conținut terț încorporat (opt-in)</h3>
          <div className="overflow-x-auto not-prose">
            <table className="min-w-full text-sm border border-brand-beige rounded-xl overflow-hidden">
              <thead className="bg-brand-cream text-brand-navy">
                <tr>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Serviciu</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Scop</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Furnizor</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Locul procesării</th>
                  <th className="p-3 text-left font-semibold border-b border-brand-beige">Detalii</th>
                </tr>
              </thead>
              <tbody className="bg-white text-brand-navy/85">
                <tr>
                  <td className="p-3">Google Maps</td>
                  <td className="p-3">Afișarea hărții cu locația cabinetului</td>
                  <td className="p-3">Google LLC / Google Ireland Ltd</td>
                  <td className="p-3">SUA / UE</td>
                  <td className="p-3"><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">policies.google.com/privacy</a></td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm italic text-brand-navy/60 mt-4">
            Fonturile utilizate pe Website (DM Sans, Manrope, Caveat, Inter, Pacifico) sunt <strong>găzduite local</strong> pe serverele noastre și nu se încarcă de la Google. Astfel nu se transmite nicio informație către terți doar prin faptul vizitării Website-ului.
          </p>

          <h2>Gestionarea modulelor cookie</h2>
          <p>
            Puteți configura modul în care Website-ul utilizează anumite categorii de module cookie plasate pe Website, respectiv vă puteți exprima consimțământul pentru utilizarea de module cookie de conținut terț sau puteți opta pentru utilizarea Website-ului fără aceste module. În acest din urmă caz, Website-ul va utiliza exclusiv modulele cookie necesare pentru funcționare.
          </p>
          <p>
            Puteți reveni oricând asupra opțiunii dumneavoastră accesând sectiunea <strong>„Preferințe cookies"</strong> din subsolul Website-ului.
          </p>
          <p>
            De asemenea, puteți modifica setările modulelor cookie, precum și șterge modulele cookie existente, accesând setările browserului pe care îl utilizați:
          </p>
          <ul>
            <li><a href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">Microsoft Edge</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">Firefox</a></li>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">Chrome Desktop</a></li>
            <li><a href="https://support.google.com/chrome/answer/95647?hl=ro&co=GENIE.Platform%3DAndroid" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">Chrome Android</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-brand-red underline">Safari</a></li>
          </ul>

          <h2>Contact</h2>
          <p>
            Ne puteți adresa orice întrebări legate de modulele cookie plasate pe Website-ul nostru prin utilizarea următoarelor date de contact:
          </p>
          <ul>
            <li><strong>Adresa poștală:</strong> {OPERATOR.address}</li>
            <li><strong>Adresa de e-mail:</strong> {OPERATOR.contactEmail}</li>
          </ul>

          <p className="mt-8">
            Pentru mai multe detalii despre modul în care prelucrăm datele dumneavoastră cu caracter personal, consultați și <Link to="/politica-confidentialitate" className="text-brand-red underline">Politica de confidențialitate</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
